/**
 * A tween small enough not to be worth a dependency.
 *
 * The hero needs three eased interpolations — the phone's spin, its blur, and
 * the orbs imploding and scattering — and nothing else on the site animates
 * from JavaScript. Pulling in an animation library for that would add ~70kB to
 * a bundle that currently ships none, so the twenty lines live here instead.
 *
 * Every tween returns its own cancel function, and callers are expected to run
 * it on unmount: a frame callback that writes to a detached node is the usual
 * way a hero like this leaks.
 */

/** Overshoot constant behind the standard "back" curve. */
const BACK = 1.70158;

export const easing = {
  linear: (t) => t,
  power2In: (t) => t * t,
  power2Out: (t) => 1 - (1 - t) * (1 - t),
  power2InOut: (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),

  /**
   * Settles past its target and comes back. `overshoot` is the same number the
   * usual `back.out(n)` takes, so 0.7 is gentle and 1.5 is pronounced.
   */
  backOut:
    (overshoot = BACK) =>
    (t) => {
      const p = t - 1;
      return 1 + (overshoot + 1) * p * p * p + overshoot * p * p;
    }
};

/**
 * Runs `onUpdate(progress)` once per frame for `duration` ms, where progress is
 * the eased 0→1 position. Always fires a final frame at exactly 1 before
 * `onComplete`, so a tween can never leave its target a fraction short.
 */
export function tween({ duration = 600, ease = easing.linear, onUpdate, onComplete }) {
  let frame = 0;
  let start = 0;
  let cancelled = false;

  const step = (now) => {
    if (cancelled) return;
    if (!start) start = now;

    const elapsed = Math.min((now - start) / duration, 1);
    if (onUpdate) onUpdate(ease(elapsed), elapsed);

    if (elapsed < 1) {
      frame = requestAnimationFrame(step);
      return;
    }

    frame = 0;
    if (onComplete) onComplete();
  };

  frame = requestAnimationFrame(step);

  return () => {
    cancelled = true;
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  };
}

/** Straight-line interpolation, for the values a tween drives. */
export const lerp = (from, to, t) => from + (to - from) * t;

export default tween;
